<template>
    <div>
        <h3 class="my-0 text-35px">Can you match the symptoms?</h3>
        <p class="font-helvetica leading-5 text-lg text-co-dark-grey mt-8 mb-20">Select and drag the word to match the image.</p>

        <div class="max-w-5xl mx-auto">
            <div class="symptom-bubbles relative flex flex-wrap mt-12">
                <div v-for="bubble in bubbles" :key="bubble.id"
                    :class="[{'hidden': bubble.answered}, bubble.class, 'lg:absolute cursor-move flex justify-center mb-2 lg:mb-0 bg-contain bg-no-repeat font-permanent_marker text-white text-center']"
                    draggable
                    @dragstart='startDrag($event, bubble.id)'
                    :ref="`bubble-${bubble.id}`">

                    <p class="my-0 text-xs md:text-base lg:text-36px" v-html="bubble.name"></p>
                </div>
            </div>

            <div class="drag-button flex justify-center lg:-mt-20 lg:-ml-6">
                <p class="my-0 mr-4 font-permanent_marker lg:text-35px text-black">Drag</p>
                <img src="/assets/img/arrow-down.png" alt="Arrow down" class="self-start w-3 lg:w-auto lg:-mt-2">
            </div>

            <div class="symptom-icons mt-8 flex flex-wrap justify-center">
                <div v-for="symptom in symptoms" :key="symptom.id"
                    :class="[{ 'correct': symptom.correct }, symptom.class, 'w-1/5 md:w-1/4 lg:w-auto mx-4 lg:mx-10 mb-12']"
                    @drop="onDrop($event, symptom.id)"
                    @dragover.prevent
                    @dragenter.prevent>

                    <div v-if="symptom.bubble" class="relative">
                        <div v-html="symptom.bubble"></div>
                        <img src="/assets/img/tick.png" alt="Tick" class="bubble-tick w-5 lg:w-16 absolute z-10">
                    </div>

                    <div v-html="symptom.svg"
                        :ref="`symptom-svg-${symptom.id}`" class="hidden"></div>
                </div>
            </div>

            <div class="pb-10 text-center">
                <a :href="$location.href" class="btn bg-co-blue border-co-blue px-8 text-white">Reset Game</a>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            bubbles: [
                { id: 1, name: 'Headache', class: 'bg-headache-bubble items-center', answered: false },
                { id: 2, name: 'Nausea<br>or<br>feeling<br>sick', class: 'bg-sickness-bubble items-center', answered: false },
                { id: 3, name: 'Loss of<br>consciousness', class: 'bg-passed-out-bubble right-0 pt-3 lg:pt-12', answered: false },
                { id: 4, name: 'Breathlessness', class: 'bg-cough-bubble pt-3 lg:pt-10', answered: false },
                { id: 5, name: 'Collapse', class: 'bg-crawling-bubble pt-6 md:pt-8 lg:pt-20', answered: false },
                { id: 6, name: 'Dizziness', class: 'bg-hearing-bubble pt-3 md:pt-4 lg:pt-16', answered: false },
            ],
            symptoms: [
                { id: 1, svg: '/assets/img/symptoms/headache.svg', class: 'headache', correct: false, bubble: '' },
                { id: 2, svg: '/assets/img/symptoms/sickness.svg', class: 'sickness', correct: false, bubble: '' },
                { id: 3, svg: '/assets/img/symptoms/passed-out.svg', class: 'passed-out self-center', correct: false, bubble: '' },
                { id: 4, svg: '/assets/img/symptoms/cough.svg', class: 'cough', correct: false, bubble: '' },
                { id: 5, svg: '/assets/img/symptoms/crawling.svg', class: 'crawling', correct: false, bubble: '' },
                { id: 6, svg: '/assets/img/symptoms/hearing.svg', class: 'hearing', correct: false, bubble: '' },
            ]
        }
    },
    beforeMount() {
        /**
         * Loops through each object in symptoms array and gets the svg data from the image path provided
         * to set it as the new value for the svg key so it can be shown in the template and removes hidden class so the svg html is visible.
         */
        this.symptoms.filter(symptom => {
            axios.get(symptom.svg)
            .then(response => {
                symptom.svg = response.data
                this.$refs[`symptom-svg-${symptom.id}`][0].classList.remove('hidden')
            })
        })
    },
    methods: {
        /**
         * Method for when someone starts dragging a bubble image and uses built-in DataTransfer to set an id
         * to be referenced in the drop zone.
         */
        startDrag(event, item) {
            event.dataTransfer.dropEffect = 'copy'
            event.dataTransfer.effectAllowed = 'copy'
            event.dataTransfer.setData('itemID', item)
        },

        /**
         * When someone places a bubble on one of the symptom images, it checks if the id set in DataTransfer matches the one
         * in the drop zone, and it it does it sets some properties to confirm this.
         */
        onDrop(event, item) {
            const itemID = event.dataTransfer.getData('itemID')

            if (itemID == item) {
                const symptom = this.symptoms.find(symptom => symptom.id == itemID)
                const bubble = this.bubbles.find(bubble => bubble.id == itemID)
                const bubble_ref = this.$refs[`bubble-${itemID}`]

                bubble.answered = true

                bubble_ref[0].removeAttribute('draggable')
                bubble_ref[0].classList.remove('cursor-move')
                bubble_ref[0].classList.add('absolute')

                symptom.correct = true
                symptom.bubble = bubble_ref[0].outerHTML
            }
        },
    }
}
</script>

<style lang="scss">
    .symptom-bubbles {
        @screen lg {
            height: 500px;
        }
    }

    .bg-sickness-bubble {
        top: 18%;
        left: 3.5%;
        z-index: 4;
    }

    .bg-hearing-bubble {
        left: 17%;
        bottom: 4%;
        z-index: 3;
    }

    .bg-cough-bubble {
        left: 26%;
        top: 35%;
        z-index: 2;
    }

    .bg-headache-bubble {
        right: 29%;
        top: 0;
        z-index: 1;
    }

    .bg-crawling-bubble {
        right: 26%;
        top: 41%;
        z-index: 10;
    }

    .bg-passed-out-bubble {
        top: 20%;
        z-index: 1;
    }

    .symptom-icons {
        svg {
            width: 100%;
            height: auto;

            @screen md {
                width: 90%;
            }

            @screen lg {
                width: auto;
            }
        }
    }

    .headache {
        &.correct {
            .correct-fill {
                fill: #DE5530;
            }
        }

        svg {
            @screen md {
                width: 108px;
            }

            @screen lg {
                width: auto;
            }
        }

        .bg-headache-bubble {
            top: -3rem;
            right: -62%;

            @screen md {
                right: -3%;
            }

            @screen lg {
                top: -7rem;
                left: 4rem;
                width: 200px;
            }

            p {
                @screen lg {
                    @apply .-mt-8;
                    @apply .text-3xl;
                }
            }
        }

        .bubble-tick {
            right: -24%;

            @screen md {
                top: 2rem;
                right: 10%;
            }

            @screen lg {
                right: -6rem;
                top: 1rem;
            }
        }
    }

    .sickness {
        &.correct {
            .correct-fill {
                fill: #33B1BA;
            }
        }

        .bg-sickness-bubble {
            top: 3rem;

            @screen md {
                top: 6rem;
                left: 30%;
            }

            @screen lg {
                top: 8rem;
                left: 1rem;
                width: 200px;
            }

            p {
                @screen lg {
                    @apply .-mt-8;
                    @apply .text-3xl;
                }
            }
        }

        .bubble-tick {
            top: 6.5rem;
            right: -1rem;

            @screen md {
                top: 11rem;
                right: 2rem;
            }

            @screen lg {
                top: 17rem;
                right: -1rem;
            }
        }
    }

    .passed-out {
        &.correct {
            .correct-fill {
                fill: #F3AB2B;
            }
        }

        svg {
            width: 78px;

            @screen md {
                width: 200px;
            }

            @screen lg {
                width: auto;
            }
        }

        .bg-passed-out-bubble {
            top: -4.5rem;
            left: -0.5rem;
            z-index: 10;

            @screen md {
                top: -5.5rem;
                left: 25%;
            }

            @screen lg {
                top: -13.4rem;
                left: 17%;
            }
        }

        .bubble-tick {
            top: -1.8rem;
            right: -0.5rem;

            @screen md {
                top: -2rem;
                right: 2rem;
            }

            @screen lg {
                top: -4.6rem;
                right: 2rem;
            }
        }
    }

    .cough {
        &.correct {
            .correct-fill {
                fill: #2690D0;
            }
        }

        svg {
            @screen md {
                width: 120px;
            }

            @screen lg {
                width: auto;
            }
        }

        .bg-cough-bubble {
            top: -2rem;
            left: -56%;

            @screen md {
                left: 0;
            }

            @screen lg {
                top: -5rem;
                left: -5rem;
                width: 290px;
            }

            p {
                @screen lg {
                    @apply .text-3xl;
                }
            }
        }

        .bubble-tick {
            right: 0;

            @screen md {
                top: 0.5rem;
                right: 3rem;
            }

            @screen lg {
                top: 0;
                right: -2rem;
            }
        }
    }

    .crawling {
        &.correct {
            .correct-fill {
                fill: #053755;
            }
        }

        svg {
            width: 72px;
            @apply .-ml-2;

            @screen md {
                width: 185px;
            }

            @screen lg {
                width: auto;
                @apply .ml-0;
            }
        }

        .bg-crawling-bubble {
            top: -3.5rem;
            right: -38%;
            z-index: 0;

            @screen md {
                right: -19%;
            }

            @screen lg {
                top: -9rem;
                right: -5rem;
                width: 178px;
            }

            p {
                @screen lg {
                    @apply .-mt-4;
                    @apply .text-3xl;
                }
            }
        }

        .bubble-tick {
            top: -0.5rem;
            right: -1rem;

            @screen md {
                top: 0.5rem;
            }

            @screen lg {
                top: 0;
                right: -3rem;
            }
        }
    }

    .hearing {
        &.correct {
            .correct-fill {
                fill: #E24745;
            }
        }

        .bg-hearing-bubble {
            top: -2rem;

            @screen md {
                top: -3rem;
                left: 50%;
            }

            @screen lg {
                top: -6rem;
                left: 4rem;
                width: 220px;
            }

            p {
                @screen lg {
                    @apply .-mt-4;
                    @apply .text-3xl;
                }
            }
        }

        .bubble-tick {
            right: 0;

            @screen md {
                top: -1rem;
                right: 1rem;
            }

            @screen lg {
                top: 1rem;
                right: -3rem;
            }
        }
    }
</style>
